import * as React from "react";
import { useLingui } from "@lingui/react/macro";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, CheckCircle2, Copy, Loader2, X, XCircle } from "lucide-react";
import { cn } from "@app/ui/lib/Utils";
import { hapticNotification } from "@app/ui/lib/Haptics";

export type ToastVariant = "default" | "success" | "error" | "warning";

export interface ToastAction {
  label: string;
  /**
   * 액션(예: "실행취소")을 눌렀을 때 실행한다. Promise 를 반환하면 버튼이 로더로 바뀌고,
   * 완료(resolve) 시 체크마크로 바뀐 뒤 토스트가 잠깐 있다가 닫힌다. 실패(reject) 시엔
   * 다시 원래 라벨로 돌아와 재시도할 수 있다. 반환값이 없으면(기존 동기 액션) 그대로 실행만 한다.
   */
  onClick: () => void | Promise<unknown>;
}

export interface ToastOptions {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  /**
   * 왼쪽에 표시할 커스텀 아이콘. 지정하면 variant 기본 아이콘 대신 이걸 쓴다
   * (예: 업데이트 안내 토스트의 설치 아이콘).
   */
  icon?: React.ComponentType<{ className?: string }>;
  /** 자동으로 닫히기까지 걸리는 시간(ms). Infinity 를 주면 자동으로 닫히지 않는다. */
  duration?: number;
  action?: ToastAction;
}

interface ToastRecord extends ToastOptions {
  id: string;
  state: "open" | "closed";
}

const DEFAULT_DURATION = 4000;
/** 복사 버튼이 있는 에러 토스트는 복사할 시간을 주려고 더 오래 유지한다. */
const COPYABLE_DURATION = 10000;

let toastCount = 0;
function nextId() {
  toastCount += 1;
  return `toast-${toastCount}`;
}

let records: ToastRecord[] = [];
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return records;
}

function dismiss(id?: string) {
  if (id === undefined) {
    records = records.map((record) => ({ ...record, state: "closed" }));
  } else {
    records = records.map((record) =>
      record.id === id ? { ...record, state: "closed" } : record
    );
  }
  emit();
}

/** 퇴장 애니메이션(duration-300)이 끝난 뒤 실제로 목록에서 제거한다. */
function remove(id: string) {
  records = records.filter((record) => record.id !== id);
  emit();
}

/** 토스트 variant → 촉각 피드백. task 사건과는 축이 달라(알림 성격) resolveTaskHaptic
 * 과 억지로 합치지 않고, 같은 "의미 → 햅틱" 리졸버 패턴만 맞춰 나란히 둔다. default
 * (단순 안내)는 진동을 넣지 않아 피로를 막고, 네이티브 아님/미로드면 알아서 no-op 한다. */
function resolveToastHaptic(variant: ToastVariant | undefined): void {
  if (variant === "success") hapticNotification("Success");
  else if (variant === "warning") hapticNotification("Warning");
  else if (variant === "error") hapticNotification("Error");
}

function show(options: ToastOptions): string {
  const id = nextId();
  records = [...records, { ...options, id, state: "open" }];
  resolveToastHaptic(options.variant);
  emit();
  return id;
}

/**
 * 어디서든 `toast(...)` 를 호출하면 트리 최상단에 마운트된 `<Toaster />` 에 알림이 뜬다.
 * Provider 로 감쌀 필요 없이 모듈 스코프 스토어를 구독하는 방식이다.
 */
function toast(options: ToastOptions | React.ReactNode) {
  if (typeof options === "string" || React.isValidElement(options)) {
    return show({ title: options });
  }
  return show(options as ToastOptions);
}

toast.success = (title: React.ReactNode, options?: Omit<ToastOptions, "title" | "variant">) =>
  show({ ...options, title, variant: "success" });
toast.error = (title: React.ReactNode, options?: Omit<ToastOptions, "title" | "variant">) =>
  show({ ...options, title, variant: "error" });
toast.warning = (title: React.ReactNode, options?: Omit<ToastOptions, "title" | "variant">) =>
  show({ ...options, title, variant: "warning" });
// 함수 참조를 그대로 대입하면 d.ts 의 namespace 안에 `export { dismiss }` 가 생겨
// 나머지 멤버(success 등)가 암시적 export 에서 빠진다. 화살표로 감싸 var 로 방출시킨다.
toast.dismiss = (id?: string) => dismiss(id);

export interface UseToastReturn {
  toast: typeof toast;
  dismiss: typeof dismiss;
}

/** 컴포넌트 안에서 `useXxx` 컨벤션으로 쓰고 싶을 때를 위한 얇은 래퍼. `toast()`를 직접 import 해도 동일하게 동작한다. */
function useToast(): UseToastReturn {
  return { toast, dismiss };
}

/** title/description 중 문자열인 것만 골라 복사용 텍스트로 합친다. JSX 노드는 뽑아낼 수 없어 제외한다. */
function resolveCopyText({
  title,
  description,
}: Pick<ToastOptions, "title" | "description">): string {
  return [title, description].filter((v): v is string => typeof v === "string").join("\n");
}

/** 우선 navigator.clipboard 를 쓰고, secure context 미지원/웹뷰 차단 시 execCommand 로 폴백한다. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

const VARIANT_ICON: Record<ToastVariant, React.ComponentType<{ className?: string }> | null> = {
  default: null,
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
};

const VARIANT_ICON_CLASSNAME: Record<ToastVariant, string> = {
  default: "",
  success: "text-green-500",
  error: "text-destructive",
  warning: "text-amber-500",
};

export interface ToastProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  variant?: ToastVariant;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** variant 기본 아이콘을 대체하는 왼쪽 아이콘. */
  icon?: React.ComponentType<{ className?: string }>;
  action?: ToastAction;
  onDismiss?: () => void;
  /** 스와이프 드래그가 시작될 때(자동 닫힘 타이머를 멈추는 용도). */
  onGestureStart?: () => void;
  /** 스와이프가 임계값 미달로 취소돼 원위치될 때(타이머 재개 용도). */
  onGestureEnd?: () => void;
}

/** 이 거리(px) 이상 오른쪽으로 쓸면 닫는다. */
const SWIPE_DISMISS_THRESHOLD = 80;
/** 이 거리에서 완전히 투명해진다(드래그 중 페이드). */
const SWIPE_FADE_DISTANCE = 200;

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      className,
      variant = "default",
      title,
      description,
      icon,
      action,
      onDismiss,
      onGestureStart,
      onGestureEnd,
      ...props
    },
    ref
  ) => {
    const { t } = useLingui();
    // 커스텀 아이콘이 있으면 그걸, 없으면 variant 기본 아이콘을 왼쪽에 그린다.
    const Icon = icon ?? VARIANT_ICON[variant];
    // 커스텀 아이콘은 variant 색이 없으니(default 는 빈 문자열) 중립 색을 준다.
    const iconClassName = icon ? "text-foreground" : VARIANT_ICON_CLASSNAME[variant];

    // 오른쪽 스와이프로 닫기(터치+마우스 통합, Pointer 이벤트). dragX 는 오른쪽 이동량(px, 0 이상).
    const [dragX, setDragX] = React.useState(0);
    const [dragging, setDragging] = React.useState(false);
    const startXRef = React.useRef(0);

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      // 버튼/링크 위에서 시작한 포인터는 드래그로 오인하지 않는다(X·복사·액션 탭 보호).
      if ((e.target as HTMLElement).closest("button, a")) return;
      startXRef.current = e.clientX;
      setDragging(true);
      onGestureStart?.();
      e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      // 오른쪽만 허용 — 왼쪽(음수)은 0 으로 고정.
      setDragX(Math.max(0, e.clientX - startXRef.current));
    };

    const handlePointerEnd = () => {
      if (!dragging) return;
      setDragging(false);
      if (dragX >= SWIPE_DISMISS_THRESHOLD) {
        // 스와이프한 현재 위치(translateX)를 유지한 채 퇴장 애니메이션에 넘긴다.
        // dragX 를 0 으로 되돌리면 토스트가 원위치로 튀었다가 slide-out 이 처음부터 재생돼 어색하다.
        // exit 키프레임(slide-out-to-right-full)은 시작점을 요소의 현재 computed transform 에서 잇는다.
        onDismiss?.();
      } else {
        setDragX(0); // transition 이 걸린 채로 0 이 돼 부드럽게 원위치.
        onGestureEnd?.();
      }
    };

    const swipeStyle: React.CSSProperties = {
      touchAction: "pan-y", // 세로 스크롤은 브라우저에, 가로 제스처만 이쪽으로.
      ...(dragX !== 0 && {
        transform: `translateX(${dragX}px)`,
        opacity: Math.max(0, 1 - dragX / SWIPE_FADE_DISTANCE),
      }),
      transition: dragging ? "none" : "transform 0.2s ease, opacity 0.2s ease",
    };

    const copyText = resolveCopyText({ title, description });
    // 에러 토스트에만, 그리고 실제로 복사할 문자열이 있을 때만 복사 버튼을 노출한다.
    const showCopy = variant === "error" && copyText.trim().length > 0;
    const [copied, setCopied] = React.useState(false);
    const copiedTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    React.useEffect(() => () => clearTimeout(copiedTimer.current), []);

    const handleCopy = async () => {
      const ok = await copyToClipboard(copyText);
      if (!ok) return; // 실패 시 조용히 무시 — 에러 토스트가 또 뜨는 재귀를 막는다.
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1500);
    };

    // 액션 버튼(실행취소 등)의 진행 상태. onClick 이 Promise 를 반환하면
    // idle → pending(로더) → done(체크) 로 흐르고, done 뒤 잠깐 있다가 토스트를 닫는다.
    const [actionState, setActionState] = React.useState<"idle" | "pending" | "done">("idle");
    const doneTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    // pending 도중 스와이프/닫기/자동닫힘으로 토스트가 사라질 수 있어, 콜백이 언마운트 후
    // setState 하지 않도록 마운트 여부를 추적한다(done 타이머도 여기서 정리).
    const mountedRef = React.useRef(true);
    React.useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        clearTimeout(doneTimerRef.current);
      };
    }, []);

    const handleAction = () => {
      if (!action || actionState !== "idle") return; // 중복 클릭 가드.
      const result = action.onClick();
      const isThenable =
        result != null && typeof (result as { then?: unknown }).then === "function";
      // 반환값이 없으면 기존 동기 액션 — 로더/체크 없이 실행만 하고 끝난다.
      if (!isThenable) return;
      // 자동닫힘 타이머를 멈춘다(필수) — 서버가 느리면 pending 중 토스트가 사라질 수 있다.
      onGestureStart?.();
      setActionState("pending");
      Promise.resolve(result).then(
        () => {
          if (!mountedRef.current) return;
          setActionState("done");
          doneTimerRef.current = setTimeout(() => onDismiss?.(), 1000);
        },
        () => {
          // 실패하면 체크 대신 원래 라벨로 돌아와 재시도 가능하게 하고, 자동닫힘을 재개한다.
          if (!mountedRef.current) return;
          setActionState("idle");
          onGestureEnd?.();
        },
      );
    };

    return (
      <div
        ref={ref}
        role="status"
        className={cn(
          "pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-2xl bg-popover/80 p-4 text-popover-foreground shadow-2xl backdrop-blur-xl",
          // 등장 슬라이드 방향(slide-in-from-*)은 위치에 따라 달라져 ToasterItem 이 className 으로 주입한다.
          "duration-300 fill-mode-forwards data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-right-full",
          className
        )}
        {...props}
        style={{ ...swipeStyle, ...props.style }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      >
        {Icon && (
          <span
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-full",
              iconClassName
            )}
          >
            <Icon className="size-4" />
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {title && <p className="text-sm font-medium leading-tight">{title}</p>}
          {description && (
            <p className="text-sm leading-snug text-muted-foreground">{description}</p>
          )}
          {action && (
            <button
              type="button"
              onClick={handleAction}
              disabled={actionState !== "idle"}
              aria-label={action.label}
              aria-busy={actionState === "pending"}
              // 로더/체크 아이콘일 때 폭이 줄어 레이아웃이 튀지 않도록 최소 폭을 준다.
              className="mt-1 inline-flex min-w-[64px] items-center justify-center self-start text-sm font-medium underline-offset-4 hover:underline disabled:no-underline"
            >
              {actionState === "pending" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : actionState === "done" ? (
                <Check className="size-4" />
              ) : (
                action.label
              )}
            </button>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {showCopy && (
            <button
              type="button"
              aria-label={copied ? t`복사됨` : t`복사`}
              onClick={handleCopy}
              className="rounded-sm opacity-60 transition-opacity hover:opacity-100 focus-visible:bg-accent"
            >
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            </button>
          )}
          <button
            type="button"
            aria-label={t`닫기`}
            onClick={onDismiss}
            className="rounded-sm opacity-60 transition-opacity hover:opacity-100 focus-visible:bg-accent"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    );
  }
);
Toast.displayName = "Toast";

export type ToasterPosition = "top-right" | "top-left" | "bottom-right" | "bottom-left";

// 오프셋을 0 으로 두고 여백은 컨테이너 패딩으로 준다. right-4 처럼 오프셋을 주면
// 폭이 100% 인 컨테이너가 화면 밖으로 밀려나 좁은 화면에서 토스트 왼쪽이 잘린다.
const POSITION_CLASSNAME: Record<ToasterPosition, string> = {
  "top-right": "top-0 right-0 items-end pt-[calc(1rem+env(safe-area-inset-top))]",
  "top-left": "top-0 left-0 items-start pt-[calc(1rem+env(safe-area-inset-top))]",
  "bottom-right": "bottom-0 right-0 items-end pb-[calc(1rem+env(safe-area-inset-bottom))]",
  "bottom-left": "bottom-0 left-0 items-start pb-[calc(1rem+env(safe-area-inset-bottom))]",
};

export interface ToasterProps {
  position?: ToasterPosition;
}

function ToasterItem({
  record,
  position,
}: {
  record: ToastRecord;
  position: ToasterPosition;
}) {
  // 상단이면 위에서, 하단이면 아래에서 슬라이드 인 — 컨테이너 위치와 방향을 맞춘다.
  const slideIn = position.startsWith("top")
    ? "data-[state=open]:slide-in-from-top-2"
    : "data-[state=open]:slide-in-from-bottom-2";
  // 복사 버튼이 뜨는 에러 토스트는 복사할 시간을 주려고 기본 수명을 늘린다(모바일엔 hover 정지가 없다).
  const canCopy =
    record.variant === "error" && resolveCopyText(record).trim().length > 0;
  const duration = record.duration ?? (canCopy ? COPYABLE_DURATION : DEFAULT_DURATION);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const start = React.useCallback(() => {
    if (!Number.isFinite(duration)) return;
    timerRef.current = setTimeout(() => dismiss(record.id), duration);
  }, [duration, record.id]);

  const stop = React.useCallback(() => {
    clearTimeout(timerRef.current);
  }, []);

  React.useEffect(() => {
    start();
    return stop;
  }, [start, stop]);

  return (
    <Toast
      className={slideIn}
      data-state={record.state}
      variant={record.variant}
      title={record.title}
      description={record.description}
      icon={record.icon}
      action={record.action}
      onMouseEnter={stop}
      onMouseLeave={start}
      onGestureStart={stop}
      onGestureEnd={start}
      onDismiss={() => dismiss(record.id)}
      onAnimationEnd={() => {
        if (record.state === "closed") remove(record.id);
      }}
    />
  );
}

/** 앱 트리 어디에나 한 번 마운트해두면 `toast(...)` 호출을 받아 알림을 띄운다. */
function Toaster({ position = "bottom-right" }: ToasterProps) {
  const items = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div
      className={cn(
        // 폭 제한은 아이템(Toast 의 max-w-sm)이 하고, 컨테이너는 정렬 축 + 좌우 여백만 담당한다.
        "pointer-events-none fixed z-[100] flex w-full flex-col gap-2 px-4",
        POSITION_CLASSNAME[position]
      )}
    >
      {items.map((record) => (
        <ToasterItem key={record.id} record={record} position={position} />
      ))}
    </div>,
    document.body
  );
}

export { Toast, Toaster, toast, useToast };
